import { ChannelsByCategory, SDKChannel } from "../types/onboarding";

// Backend/admin data tags document-capture fields inconsistently — the field
// renderer already treats "image" and "file" as file inputs alongside
// "upload", so flow-routing decisions must recognize the same set or a channel
// tagged "image"/"file" silently falls back to the number-entry screen.
export function isUploadFieldType(type: unknown): boolean {
  return type === "upload" || type === "image" || type === "file";
}

function isChannelList(value: unknown): value is (SDKChannel | string)[] {
  return Array.isArray(value);
}

/**
 * Returns initialize/'s channels as {data_verification, document_verification}
 * per country, whichever shape the backend sent.
 *
 * Backends without the category split send one flat list per country, and
 * reading .data_verification off that list crashed the country screen
 * ("Cannot read properties of undefined (reading 'length')"), leaving the
 * widget stuck after "Get Started". A flat list is split the same way
 * isChannelDocumentFlow decides per channel: any upload field means a
 * document scan; anything else, including a legacy string channel, is a
 * number-entry lookup. A country whose value is neither shape is dropped.
 */
export function normalizeChannels(raw: unknown): Record<string, ChannelsByCategory> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};

  const grouped: Record<string, ChannelsByCategory> = {};
  for (const [country, value] of Object.entries(raw as Record<string, unknown>)) {
    if (isChannelList(value)) {
      grouped[country] = { data_verification: [], document_verification: [] };
      for (const channel of value) {
        const isDocument =
          typeof channel !== "string" &&
          (channel.fields || []).some((f: any) => isUploadFieldType(f.type));
        grouped[country][isDocument ? "document_verification" : "data_verification"].push(channel);
      }
    } else if (value && typeof value === "object") {
      const byCategory = value as Partial<ChannelsByCategory>;
      grouped[country] = {
        data_verification: isChannelList(byCategory.data_verification) ? byCategory.data_verification : [],
        document_verification: isChannelList(byCategory.document_verification)
          ? byCategory.document_verification
          : [],
      };
      if (typeof byCategory.code === "string") grouped[country].code = byCategory.code;
      if (typeof byCategory.name === "string") grouped[country].name = byCategory.name;
    }
  }
  return grouped;
}

// Sent when no country was chosen (liveness only); the backend stores it
// without treating it as a real country.
const NO_COUNTRY = "global";

/**
 * The country value sent with a check: the ISO code initialize/ gave for it,
 * else the country key itself, which the backend resolves by name. Never a
 * guess: deriving a code from the name sent Togo as TO (Tonga) and Cote
 * d'Ivoire as CO (Colombia), so the OCR rejected their passports as issued by
 * the wrong country.
 */
export function countryCodeFor(channels: Record<string, ChannelsByCategory>, country: string): string {
  if (!country || country === NO_COUNTRY) return NO_COUNTRY;
  return channels[country]?.code || country;
}

/** The name to show for a country: initialize/'s, else its key capitalised. */
export function countryLabel(channels: Record<string, ChannelsByCategory>, country: string): string {
  const name = channels[country]?.name;
  if (name) return name;
  return country.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function searchable(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

/**
 * The countries whose name contains `query` (case and accents ignored),
 * sorted A to Z by display name. An empty query returns every country.
 */
export function filterCountries(channels: Record<string, ChannelsByCategory>, query: string): string[] {
  const needle = searchable(query);
  return Object.keys(channels)
    .map((country) => ({ country, label: countryLabel(channels, country) }))
    .filter(({ country, label }) => !needle || searchable(label).includes(needle) || searchable(country).includes(needle))
    .sort((a, b) => a.label.localeCompare(b.label))
    .map(({ country }) => country);
}
